import { z } from 'zod';

// Parent fields are optional at the schema level - we validate completeness in superRefine
const parentSchema = z.object({
  firstName: z.string().optional().or(z.literal('')),
  lastName: z.string().optional().or(z.literal('')),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  isPrimary: z.boolean(),
}).optional().nullable();

const childSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  dateOfBirth: z.string().optional().or(z.literal('')),
  gender: z.enum(['MALE', 'FEMALE'], { message: 'Gender is required' }),
});

// Helper to check if a parent has data
function hasParentData(parent: z.infer<typeof parentSchema>) {
  if (!parent) return false;
  const firstName = parent.firstName?.trim();
  const lastName = parent.lastName?.trim();
  return firstName && lastName;
}

export const addFamilySchema = z.object({
  father: parentSchema,
  mother: parentSchema,
  children: z.array(childSchema).optional(),
}).superRefine((data, ctx) => {
  const hasFather = hasParentData(data.father);
  const hasMother = hasParentData(data.mother);

  // At least one parent must be provided
  if (!hasFather && !hasMother) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'At least one parent must be provided',
      path: ['father', 'firstName'],
    });
    return;
  }

  // At least one parent with data must be primary
  const fatherIsPrimary = hasFather && data.father?.isPrimary;
  const motherIsPrimary = hasMother && data.mother?.isPrimary;

  if (!fatherIsPrimary && !motherIsPrimary) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'At least one parent must be marked as primary',
      path: ['father', 'isPrimary'],
    });
    return;
  }

  // Primary parents must have email
  if (fatherIsPrimary && (!data.father?.email || data.father.email.trim() === '')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Primary parent must have an email address',
      path: ['father', 'email'],
    });
  }

  if (motherIsPrimary && (!data.mother?.email || data.mother.email.trim() === '')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Primary parent must have an email address',
      path: ['mother', 'email'],
    });
  }
});

export type AddFamilyFormValues = z.infer<typeof addFamilySchema>;

export const defaultFormValues = {
  father: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    isPrimary: true,
  },
  mother: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    isPrimary: true,
  },
  children: [] as any[],
};
